/*
Animaglass Global Phone Model Filter
------------------------------------

Controls the global device selection for the store.

Main features:
- Stores selected model in localStorage / cookie fallback
- Automatically applies phone filter to:
  /collections/all
  /collections/cases
  /collections/backplates
- Forces single-select behavior for phone model filters
- Restores default model (iPhone 17) after reset/clear
- Patches links so navigation keeps the selected model

Storage key:
animaglass_phone_model
// Created entirely by Atlas (ChatGPT AI assistant)
*/
(function () {
  const STORAGE_KEY = 'animaglass_phone_model';
  const FILTER_PARAM = 'filter.p.m.custom.phone_model';
  const DEFAULT_MODEL = 'iPhone 17';

  const normalizePath = (p) => (p.length > 1 ? p.replace(/\/+$/, '') : p);
  const targetPaths = new Set([
    '/collections/all',
    '/collections/cases',
    '/collections/backplates'
  ]);

  // ---------- storage (localStorage with cookie fallback) ----------
  const canUseLocalStorage = (() => {
    try {
      const t = '__ag__';
      localStorage.setItem(t, '1');
      localStorage.removeItem(t);
      return true;
    } catch (e) {
      return false;
    }
  })();

  const getCookie = (name) => {
    const m = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)')
    );
    return m ? decodeURIComponent(m[1]) : null;
  };

  const setCookie = (name, value, days = 90) => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  };

  const safeGet = () => {
    if (canUseLocalStorage) {
      try { return localStorage.getItem(STORAGE_KEY); } catch (e) {}
    }
    return getCookie(STORAGE_KEY);
  };

  const safeSet = (v) => {
    if (canUseLocalStorage) {
      try { localStorage.setItem(STORAGE_KEY, v); return; } catch (e) {}
    }
    setCookie(STORAGE_KEY, v);
  };

  const getCurrentModel = () => safeGet() || DEFAULT_MODEL;

  // ---------- helper: add model param to eligible URLs ----------
  const withModelParam = (href, model) => {
    try {
      const u = new URL(href, window.location.origin);
      if (u.origin !== window.location.origin) return null;

      const p = normalizePath(u.pathname);
      if (!targetPaths.has(p)) return null;

      // enforce single param on the link itself
      u.searchParams.delete(FILTER_PARAM);
      u.searchParams.set(FILTER_PARAM, model);
      return u.toString();
    } catch (e) {
      return null;
    }
  };

// ---------- helper: restore stored model in URL (or fallback default only if nothing is stored) ----------
const restoreStoredModel = () => {
  const model = safeGet() || DEFAULT_MODEL;
  safeSet(model);
  const u = new URL(window.location.href);
  u.searchParams.delete(FILTER_PARAM);
  u.searchParams.set(FILTER_PARAM, model);
  window.location.replace(u.toString());
};

// ---------- helper: if phone filter missing on target collection pages, restore stored model ----------
const ensurePhoneFilterPresent = () => {
  const p = normalizePath(window.location.pathname);
  if (!targetPaths.has(p)) return;

  const u = new URL(window.location.href);
  if (!u.searchParams.has(FILTER_PARAM)) {
    // This happens after "Reset/Clear all" or removing facets.
    restoreStoredModel();
  }
};

  // ---------- Phase 2A: patch links so navigation goes filtered (no flicker) ----------
  const patchLinksOnce = () => {
    const model = getCurrentModel();
    document.querySelectorAll('a[href]').forEach((a) => {
      const patched = withModelParam(a.getAttribute('href') || a.href, model);
      if (patched) a.href = patched;
    });
  };

  // Patch links on initial load (site-wide)
  patchLinksOnce();

  // Patch right before navigation (covers dynamic menus/sections)
  document.addEventListener('click', (e) => {
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;

    const model = getCurrentModel();
    const patched = withModelParam(a.getAttribute('href') || a.href, model);
    if (!patched) return;

    if (a.href === patched) return;
    a.href = patched;
  }, true);

  // ---------- Only on the 3 collection pages ----------
  const path = normalizePath(window.location.pathname);
  if (!targetPaths.has(path)) return;

  // ---------- 1) on-load: store from URL + enforce single in URL ----------
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const values = params.getAll(FILTER_PARAM);

  if (values.length > 0) {
    const selected = values[values.length - 1];
    safeSet(selected);

    if (values.length > 1) {
      params.delete(FILTER_PARAM);
      params.set(FILTER_PARAM, selected);
      window.location.replace(url.toString());
      return;
    }
  } else {
    // no param -> apply stored/default
    const model = getCurrentModel();
    params.set(FILTER_PARAM, model);
    safeSet(model);
    window.location.replace(url.toString());
    return;
  }

  // ---------- 2) checkbox facet: force single-select + never allow "none selected" ----------
  const handleChange = (e) => {
    const el = e.target;
    if (!el || el.tagName !== 'INPUT') return;
    if (el.type !== 'checkbox') return;
    if (el.name !== FILTER_PARAM) return;

    const chosen = el.value;
    if (!chosen) return;

    // If user clicks the already-selected model again and Shopify tries to uncheck it,
    // immediately restore it so phone filter can never be empty.
    if (!el.checked) {
      el.checked = true;
      return;
    }

    // uncheck others (radio-like)
    document
      .querySelectorAll(`input[type="checkbox"][name="${CSS.escape(FILTER_PARAM)}"]`)
      .forEach((cb) => { if (cb !== el) cb.checked = false; });

    safeSet(chosen);
    patchLinksOnce();
  };

  document.addEventListener('change', handleChange, true);
// ---------- 3) Reset/Clear-all: clear other filters but always preserve the stored phone model ----------
  // Works for both full reload and AJAX updates:
  // - If theme changes URL via History API, we detect it.
  // - If reset triggers navigation, we check after.
  const looksLikeClearClick = (node) => {
    const text = ((node && node.textContent) || '').trim().toLowerCase();
    const aria = ((node && node.getAttribute && node.getAttribute('aria-label')) || '').toLowerCase();
    const href = ((node && node.getAttribute && node.getAttribute('href')) || '') || '';
    return (
      text.includes('clear') || text.includes('reset') ||
      aria.includes('clear') || aria.includes('reset') ||
      href.includes('clear') || href.includes('reset')
    );
  };

   document.addEventListener('click', (e) => {
    const el = e.target && e.target.closest ? e.target.closest('a,button') : null;
    if (!el) return;

    const href = el.getAttribute && el.getAttribute('href') ? el.getAttribute('href') : '';
    const isClearReset = looksLikeClearClick(el);

    // Only take over true reset/clear controls
    if (!isClearReset) return;

    const model = getCurrentModel();
    const u = new URL(window.location.href);

    // Remove all current filter params except our custom phone model
    const keysToDelete = [];
    u.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.') && key !== FILTER_PARAM) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => u.searchParams.delete(key));

    // Force our stored/current phone model to remain
    u.searchParams.delete(FILTER_PARAM);
    u.searchParams.set(FILTER_PARAM, model);

    // Stop Shopify's normal reset behavior and go directly to the correct URL
    e.preventDefault();
    e.stopPropagation();
    window.location.assign(u.toString());
  }, true);

  // Watch URL changes in AJAX facet themes
  const _pushState = history.pushState;
  history.pushState = function () {
    _pushState.apply(this, arguments);
    setTimeout(() => ensurePhoneFilterPresent(), 0);
  };

  const _replaceState = history.replaceState;
  history.replaceState = function () {
    _replaceState.apply(this, arguments);
    setTimeout(() => ensurePhoneFilterPresent(), 0);
  };

  window.addEventListener('popstate', () => {
    setTimeout(() => ensurePhoneFilterPresent(), 0);
  });

})();